import { CloseOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, theme, Typography } from 'antd';
import { FC, PropsWithChildren, useRef } from 'react';
import { Rnd } from 'react-rnd';

const { Title } = Typography;

const TITLE_BAR_HEIGHT = 30;

const titleBarClass = (bgColor: string) => css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${TITLE_BAR_HEIGHT}px;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 8px;
  background-color: ${bgColor};
  border-radius: 0.25rem 0.25rem 0 0;
  opacity: 0.9;
`;

const floatingWindowBackgroundClass = (boxShadow: string) => css`
  position: absolute;
  height: calc(100% - ${TITLE_BAR_HEIGHT}px);
  width: 100%;
  border-radius: 0.25rem 0.25rem 0 0;
  border-width: 0px;
  border-style: solid;
  box-shadow: ${boxShadow};
  top: ${TITLE_BAR_HEIGHT}px;
`;

const contentStyle = css`
  position: relative;
  top: ${TITLE_BAR_HEIGHT}px;
  height: calc(100% - ${TITLE_BAR_HEIGHT}px);
  width: 100%;
  opacity: 0.99;
  overflow-y: auto;
  padding: 1em;
`;

export const FloatingWindow: FC<
  PropsWithChildren<{
    title: string;
    minWidth?: number;
    minHeight?: number;
    isOpen: boolean;
    onClose?: () => void;
    unmountOnClose?: boolean; // when `false`, preserves the position of the floating window after closing and repoening
    defaultWidth?: number;
    defaultHeight?: number;
  }>
> = ({
  children,
  title,
  isOpen,
  minWidth = 260,
  minHeight = 200,
  onClose,
  unmountOnClose = true,
  defaultHeight,
  defaultWidth,
}) => {
  const {
    token: {
      colorBgContainer,
      colorBgElevated,
      colorText,
      colorBorder,
      boxShadow,
    },
  } = theme.useToken();

  const titleClassname = titleBarClass(colorBgElevated);

  // parent element of RnD component must *not* have display: 'none' when it's intially mounted
  // therefore we return null until user initially opens the floating window
  const hasBeenOpened = useRef(isOpen);

  if (isOpen) {
    hasBeenOpened.current = true;
  }

  if ((!isOpen && unmountOnClose) || !hasBeenOpened.current) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        display: !isOpen ? 'none' : undefined,
      }}
    >
      <div style={{ position: 'relative', top: 0, left: 0, zIndex: 9000 }}>
        <Rnd
          default={{
            x: 640,
            y: 480,
            width: defaultWidth ?? 360,
            height: defaultHeight ?? 320,
          }}
          visible={isOpen}
          minWidth={`${minWidth}px`}
          minHeight={`${minHeight}px`}
          dragHandleClassName={titleClassname}
          bounds="window"
          scale={1.01} // Hack to prevent scroll bars from appearing over main view if floating window is dragged to right/bottom edges
        >
          <div
            className={floatingWindowBackgroundClass(boxShadow)}
            style={{ borderColor: colorBorder }}
          >
            <div className={titleClassname}>
              <Title style={{ marginLeft: '20px', marginTop: '8px' }} level={5}>
                {title}
              </Title>
              <Button
                size="small"
                icon={<CloseOutlined style={{ fontSize: '12px' }} />}
                onClick={() => onClose?.()}
                style={{ backgroundColor: colorBgElevated }}
              />
            </div>
            <div
              className={contentStyle}
              style={{ color: colorText, backgroundColor: colorBgContainer }}
            >
              {isOpen && children}
            </div>
          </div>
        </Rnd>
      </div>
    </div>
  );
};
